import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { AppNotification } from '../api/notificationsApi';
import { notificationsApi } from '../api/notificationsApi';
import { chatApi } from '../api/chatApi';
import { useNotifications } from '../contexts/NotificationsContext';
import type { NotificationOverlay } from '../components/notifications/NotificationModals';
import { handleNotificationAction } from '../utils/notificationNavigation';
import { parseNotificationData } from '../utils/notificationDisplay';

async function markChatThreadReadFromNotification(notification: AppNotification) {
  if (notification.type !== 'CHAT_MESSAGE') {
    return;
  }
  const chatId = parseNotificationData(notification.data).chatId;
  if (typeof chatId !== 'string' || !chatId.trim()) {
    return;
  }
  try {
    await chatApi.markAsRead(chatId);
    window.dispatchEvent(new CustomEvent('ebms:notifications-changed'));
  } catch {
    // chat list refresh is best-effort
  }
}

export function useNotificationActions() {
  const navigate = useNavigate();
  const { refreshCounts, adjustUnread } = useNotifications();
  const [overlay, setOverlay] = useState<NotificationOverlay | null>(null);

  const openNotification = useCallback(
    async (notification: AppNotification, options?: { closeDropdown?: () => void }) => {
      options?.closeDropdown?.();

      try {
        if (!notification.isRead) {
          await notificationsApi.markAsRead(notification.id);
          adjustUnread(-1);
        }
        await markChatThreadReadFromNotification(notification);
      } catch {
        toast.error('Không thể đánh dấu đã đọc');
      }

      await handleNotificationAction(notification, {
        navigate,
        openHtmlDocument: (title, htmlContent) => setOverlay({ kind: 'html', title, htmlContent }),
        openTextDetail: (title, message) => setOverlay({ kind: 'text', title, message }),
      });
    },
    [navigate, adjustUnread],
  );

  const markAllRead = useCallback(
    async (buildingId?: string) => {
      try {
        await notificationsApi.markAllAsRead(buildingId);
        await refreshCounts();
        toast.success('Đã đánh dấu tất cả là đã đọc');
      } catch {
        toast.error('Không thể đánh dấu đã đọc');
      }
    },
    [refreshCounts],
  );

  const deleteNotification = useCallback(
    async (notification: AppNotification) => {
      try {
        await notificationsApi.delete(notification.id);
        if (!notification.isRead) {
          adjustUnread(-1);
        }
        await refreshCounts();
      } catch {
        toast.error('Không thể xóa thông báo');
        throw new Error('delete failed');
      }
    },
    [adjustUnread, refreshCounts],
  );

  const closeOverlay = useCallback(() => setOverlay(null), []);

  return {
    overlay,
    closeOverlay,
    openNotification,
    markAllRead,
    deleteNotification,
    refreshCounts,
  };
}
